/**
 * Duração legível em pt-BR para a Recarga de Planos (spec 036).
 *
 * O `consumer_log` grava `start_time`/`end_time` em UTC (spec 029); aqui só interessa o
 * INTERVALO, então o fuso é irrelevante — a exibição do instante continua no `dateFormater`.
 */

const SECOND = 1000;

/** Segundos com uma casa, no formato pt-BR: `4,2 s`. */
export const formatSeconds = (seconds?: number | null): string => {
  if (seconds === undefined || seconds === null) return "-";
  return `${seconds.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} s`;
};

/**
 * `4 min 12 s` · `48 s` · `1 h 04 min`. Sem início, `-` (nunca "0 s": ausência não é zero).
 * Sem fim, mede até `now` — é o tempo decorrido de um processo em curso.
 */
export const formatDuration = (
  start?: string | Date | null,
  end?: string | Date | null,
  now: Date = new Date()
): string => {
  if (!start) return "-";

  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : now.getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return "-";

  const totalSeconds = Math.max(0, Math.round((endMs - startMs) / SECOND));

  if (totalSeconds < 60) return `${totalSeconds} s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes < 60) {
    return `${minutes} min ${String(seconds).padStart(2, "0")} s`;
  }

  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, "0")} min`;
};

/** "há 3 s" / "há 2 min" — usado no caption que torna o polling visível (RN09). */
export const formatElapsed = (seconds: number): string => {
  if (seconds < 60) return `há ${Math.max(0, Math.round(seconds))} s`;
  return `há ${Math.floor(seconds / 60)} min`;
};

/** Estimativa arredondada para cima em minutos, ou em segundos quando abaixo de um minuto. */
export const formatEstimate = (seconds: number): string => {
  if (seconds < 60) return `~${Math.max(1, Math.round(seconds))} s`;
  return `~${Math.ceil(seconds / 60)} min`;
};
